import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import AccionaTableTabs from '../../../../../components/AccionaTableTabs';
import RemoveModal from '../../../../../components/RemoveModal/index';
import { MainContext } from '../../../../../context/MainContext';
import { getScannedDocumentByMysqlIDAndPid } from '../../../../../services/api/scannedDocuments';
import * as noValidatedScannedDocumentsActions from '../../../../../services/redux/noValidatedScannedDocuments/actions';
import * as validatedScannedDocumentsActions from '../../../../../services/redux/validatedScannedDocuments/actions';
import {
  BUTTONS_NO_VALIDATED,
  BUTTONS_VALIDATED,
  COLUMNS_HEADERS_NO_VALIDATED,
  COLUMNS_HEADERS_VALIDATED,
} from './columns';
import AccionaIcon from '../../../../../components/AccionaIcon';
import styles from './index.module.scss';
import AccionaLabel from '../../../../../components/AccionaLabel';

const Tabs = {
  VALIDATED: 'validated',
  NO_VALIDATED: 'noValidated',
};

const { labTr } = styles;

const ScannedDocumentsList = ({
  getValidatedScannedDocumentsList,
  getNoValidatedScannedDocumentsList,
  deleteValidatedScannedDocument,
  deleteNoValidatedScannedDocument,
  errorMessages,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { idDetail: id, id: pid } = useParams();
  const { project } = useOutletContext();
  const [docketFilter, setDocketFilter] = useState(null);
  const { setLoading, setError } = useContext(MainContext);
  const [selectedTab, setSelectedTab] = useState('validated');
  const [loadingScannedDocumentByMysql, seLoadingScannedDocumentByMysql] =
    useState(Boolean(id));

  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const initialFilters = useMemo(
    () => (docketFilter ? { docket_number: docketFilter } : undefined),
    [docketFilter],
  );

  const initialFiltersNotValidated = useMemo(
    () => ({ sortable: 'fileName-ASC' }),
    [],
  );

  const onClickDeleteIcon = (scannedDocument) => {
    setSelectedRows([scannedDocument]);
    setShowRemoveModal(true);
  };
  const onClickEditIcon = (scannedDocument) => {
    navigate(
      `/app/project/${project.id_project}/quality/ocr/scannedDocuments/update/${
        scannedDocument.model
      }/${scannedDocument.id}/${scannedDocument.fileName}${
        scannedDocument.model === 'delivery' ? `/${selectedTab}` : ''
      }`,
    );
  };
  const onClickDataIcon = (scannedDocument) =>
    navigate(
      `/app/project/${project.id_project}/quality/ocr/scannedDocuments/document/${scannedDocument.model}/${scannedDocument.id}/${scannedDocument.mysqlId}/${scannedDocument.fileName}`,
    );

  const onClickEheIcon = (scannedDocument) => {
    navigate(
      `/app/project/${pid}/quality/normative/${
        project.normative_data?.name?.toLowerCase() === 'ehe' ? 'ehe' : 'coe'
      }/traceability?did=${scannedDocument.docket_number}&plant=${
        scannedDocument.delivery_note_data.plant
      }`,
    );
  };
  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    if (selectedTab === 'validated') {
      deleteValidatedScannedDocument(selectedRows);
    } else {
      deleteNoValidatedScannedDocument(selectedRows);
    }
    setSelectedRows([]);
  };

  const onTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedRows([]);
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [selectedTab]);

  useEffect(() => {
    if (errorMessages) {
      setError({
        status: 500,
        errorType: 'GET_SCANNED_DOCUMENT_ERROR',
      });
    }
  }, [errorMessages, setError]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      seLoadingScannedDocumentByMysql(true);
      const [sdData] = (await getScannedDocumentByMysqlIDAndPid(id, pid)).data
        .data;
      if (sdData) {
        setDocketFilter(sdData.docket_number);
      }
      setLoading(false);
      seLoadingScannedDocumentByMysql(false);
    };
    if (id) {
      fetchData().catch((error) => {
        setLoading(false);
        setError({
          status: 500,
          errorType: 'GET_SCANNED_DOCUMENT_ERROR',
        });
      });
    }
  }, [id, navigate, setError, setLoading, pid]);

  if (id && loadingScannedDocumentByMysql) {
    return null;
  }

  return (
    <>
      <AccionaTableTabs
        selectedRows={selectedRows}
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        handleOnBulkDeleting={handleOnBulkDeleting}
        tabs={[Tabs.VALIDATED, Tabs.NO_VALIDATED]}
        title={intl.formatMessage({ id: 'scannedDocuments' })}
        onBackRoute={`/app/project/${project.id_project}/quality/ocr`}
        actions={
          <>
            <AccionaButton
              id="deleteBtn"
              disabled={!selectedRows.length}
              onClick={() => {
                setShowRemoveModal(true);
              }}
              msg={intl.formatMessage({ id: 'button.delete' })}
              classIcon="icon fas fa-trash-alt"
            />
          </>
        }
        render={() => {
          switch (selectedTab) {
            case Tabs.VALIDATED:
              return (
                <AccionaTable
                  showTitle={false}
                  setLoading={setLoading}
                  allowClear
                  allowClearStyle={{
                    position: 'absolute',
                    top: -61,
                    right: 15,
                  }}
                  onClearFilter={setDocketFilter}
                  initialFilters={initialFilters}
                  key={Tabs.VALIDATED}
                  columns={COLUMNS_HEADERS_VALIDATED}
                  getItems={getValidatedScannedDocumentsList}
                  entity="validatedScannedDocuments"
                  expandable={{
                    expandedRowRender: (record) => {
                      return (
                        <tr className={labTr}>
                          <td />
                          <td />
                          <td>{record.laboratoryNoteSD.supplier}</td>
                          <td>{record.laboratoryNoteSD.arriving_date}</td>
                          <td>
                            <AccionaIcon
                              className="btn btn-darkgray"
                              //onClick={() => viewAction(x[columns.collapsable])}
                              classIcon="icon fas fa-eye"
                              toolTip={intl.formatMessage({
                                id: BUTTONS_VALIDATED.viewAction.tooltip,
                              })}
                              onClick={() =>
                                onClickDataIcon(record.laboratoryNoteSD)
                              }
                            />
                          </td>
                          <td />
                        </tr>
                      );
                    },
                    rowExpandable: (record) => record.laboratoryNoteSD,
                  }}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  isSelectionDisabled={(record) => {
                    return record.isLotified || record.isAssayed;
                  }}
                  isRowChecked={(record) => {
                    return selectedRows.some((r) => r.id === record.id);
                  }}
                  actions={(record) => {
                    return (
                      <>
                        <AccionaTableButton
                          onClick={() => onClickDataIcon(record)}
                          type="view"
                          toolTip={intl.formatMessage({
                            id: BUTTONS_VALIDATED.viewAction.tooltip,
                          })}
                        />

                        {record.isLotified || record.isAssayed || (
                          <AccionaTableButton
                            onClick={() => onClickEditIcon(record)}
                            type="edit"
                            toolTip={intl.formatMessage({
                              id: BUTTONS_VALIDATED.editAction.tooltip,
                            })}
                          />
                        )}
                        <AccionaTableButton
                          onClick={() => onClickEheIcon(record)}
                          type="link"
                          toolTip={intl.formatMessage({
                            id: `${
                              project.id_normative === 1 ? 'ehe' : 'coe'
                            }Action.record`,
                          })}
                        />
                        {record.isLotified || record.isAssayed || (
                          <AccionaTableButton
                            onClick={() => onClickDeleteIcon(record)}
                            type="delete"
                            toolTip={intl.formatMessage({
                              id: BUTTONS_VALIDATED.deleteAction.tooltip,
                            })}
                          />
                        )}
                        {record.isLotified || record.isAssayed ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                            }}
                          >
                            {!record.isLotified || (
                              <AccionaLabel>
                                {intl.formatMessage({ id: 'lotified' })}
                              </AccionaLabel>
                            )}
                            {!record.isAssayed || (
                              <AccionaLabel>
                                {intl.formatMessage({ id: 'assayed' })}
                              </AccionaLabel>
                            )}
                          </div>
                        ) : null}
                      </>
                    );
                  }}
                />
              );
            case Tabs.NO_VALIDATED:
              return (
                <AccionaTable
                  key={Tabs.NO_VALIDATED}
                  columns={COLUMNS_HEADERS_NO_VALIDATED}
                  initialFilters={initialFiltersNotValidated}
                  getItems={getNoValidatedScannedDocumentsList}
                  entity="noValidatedScannedDocuments"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  showTitle={false}
                  dataParseFunction={(nv) => {
                    return nv.map((e) => ({
                      ...e,
                      createdAt: moment(e.createdAt, 'DD-MM-YYYY'),
                    }));
                  }}
                  actions={(nv) => (
                    <>
                      <AccionaTableButton
                        onClick={() => onClickEditIcon(nv)}
                        type="edit"
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.editAction.tooltip,
                        })}
                      />
                      <AccionaTableButton
                        onClick={() => onClickDeleteIcon(nv)}
                        type="delete"
                        toolTip={intl.formatMessage({
                          id: BUTTONS_NO_VALIDATED.deleteAction.tooltip,
                        })}
                      />
                    </>
                  )}
                />
              );
            default:
              return null;
          }
        }}
      />
      <RemoveModal
        entity="scannedDocuments"
        selectedRows={selectedRows}
        msg={intl.formatMessage({
          id:
            selectedRows.length > 1
              ? 'delete.scannedDocuments'
              : 'delete.scannedDocument',
        })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      {/*       <ViewImage
        img={selectedImg}
        visible={selectedImg}
        onCancel={onCancelViewImg}
      /> */}
    </>
  );
};

const mapStateToProps = ({
  validatedScannedDocuments,
  noValidatedScannedDocuments,
}) => ({
  errorMessages:
    validatedScannedDocuments.errorMessages ||
    noValidatedScannedDocuments.errorMessages,
});

const matchDispachToProps = {
  getValidatedScannedDocumentsList:
    validatedScannedDocumentsActions.getValidatedScannedDocuments,
  getNoValidatedScannedDocumentsList:
    noValidatedScannedDocumentsActions.getNoValidatedScannedDocuments,
  deleteNoValidatedScannedDocument:
    noValidatedScannedDocumentsActions.deleteNoValidatedScannedDocument,
  deleteValidatedScannedDocument:
    validatedScannedDocumentsActions.deleteValidatedScannedDocument,
};

ScannedDocumentsList.defaultProps = {
  errorMessages: {},
  selectedRows: [],
};

ScannedDocumentsList.propTypes = {
  getValidatedScannedDocumentsList: PropTypes.func.isRequired,
  getNoValidatedScannedDocumentsList: PropTypes.func.isRequired,
  deleteValidatedScannedDocument: PropTypes.func.isRequired,
  deleteNoValidatedScannedDocument: PropTypes.func.isRequired,
  errorMessages: PropTypes.shape({
    status: PropTypes.number,
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.object),
};

export default connect(
  mapStateToProps,
  matchDispachToProps,
)(injectIntl(ScannedDocumentsList));
